"use client"

import { useState } from 'react'
import { Star, ChevronLeft, ChevronRight } from 'lucide-react'

interface Testimonial {
  name: string
  location: string
  text: string
  rating: number
  homeType: string
}

interface TestimonialsSectionProps {
  testimonials?: Testimonial[]
  cityName?: string
}

const realTestimonials: Testimonial[] = [
  {
    name: "Maria Kaesberg",
    location: "Northern Arizona",
    text: "R.J. is Awesome! He is easy going and gentle, a really good sales person who listens to hear what your needs and desires are, then leads you in the best direction. We really like and appreciate him!",
    rating: 5,
    homeType: "Featured Review"
  },
  {
    name: "Doyle and Bobbi Graber",
    location: "Northern Arizona",
    text: "We look forward to working with RJ in the future if and when we purchase a home at your location. RJ was very pleasant to work with and took the time to explain the whole process to us.",
    rating: 5,
    homeType: "Customer"
  },
  {
    name: "Ken Alford",
    location: "Northern Arizona",
    text: "Both R.J. and the Southwest Team are great to work with. They are honest and responsive, and don't use high pressure sales tactics. Highly recommend using them.",
    rating: 5,
    homeType: "Customer"
  },
  {
    name: "Peter Spitzer",
    location: "Northern Arizona",
    text: "R.J. was a very good salesman. He was knowledgeable about the floor plans and building codes in various counties. We would recommend him to others buying a home.",
    rating: 5,
    homeType: "Customer"
  },
  {
    name: "Michael W",
    location: "Northern Arizona",
    text: "I have not bought yet, but R.J. is amazing. No pressure, very knowledgeable, and really cares about finding the right fit.",
    rating: 5,
    homeType: "Customer"
  },
  {
    name: "Helen",
    location: "Northern Arizona",
    text: "R.J. is the best! He's helped us through every step and made the process so easy. Couldn't be happier with our new home.",
    rating: 5,
    homeType: "Customer"
  }
]

export function TestimonialsSection({ testimonials = realTestimonials, cityName }: TestimonialsSectionProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const testimonialsToUse = testimonials || realTestimonials

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonialsToUse.length)
  }

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonialsToUse.length) % testimonialsToUse.length)
  }

  return (
    <section className="py-16 bg-gradient-to-br from-[#c88546]/5 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
            Real Stories from Happy Homeowners
            {cityName && <span className="block text-xl text-gray-700 mt-2">Serving {cityName} and Surrounding Areas</span>}
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            See why families throughout Northern Arizona choose us for their manufactured homes
          </p>

          {/* Desktop Grid View */}
          <div className="hidden md:grid md:grid-cols-3 gap-6 mb-8">
            {testimonialsToUse.slice(0, 3).map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic text-sm leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div className="border-t pt-3">
                  <p className="font-bold text-gray-900">— {testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.location}</p>
                  <p className="text-sm text-[#c88546] font-semibold mt-1">{testimonial.homeType}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Carousel View */}
          <div className="md:hidden">
            <div className="relative bg-white rounded-xl p-6 shadow-lg">
              <div className="flex gap-1 mb-3">
                {[...Array(testimonialsToUse[currentIndex].rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 italic leading-relaxed">
                "{testimonialsToUse[currentIndex].text}"
              </p>
              <div className="border-t pt-3">
                <p className="font-bold text-gray-900">— {testimonialsToUse[currentIndex].name}</p>
                <p className="text-sm text-gray-600">{testimonialsToUse[currentIndex].location}</p>
                <p className="text-sm text-[#c88546] font-semibold mt-1">{testimonialsToUse[currentIndex].homeType}</p>
              </div>

              {/* Navigation Buttons */}
              <div className="absolute inset-y-0 left-0 flex items-center">
                <button
                  onClick={prevTestimonial}
                  className="bg-white/90 p-2 rounded-r shadow-md hover:bg-white transition-colors -ml-1"
                  aria-label="Previous testimonial"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-700" />
                </button>
              </div>
              <div className="absolute inset-y-0 right-0 flex items-center">
                <button
                  onClick={nextTestimonial}
                  className="bg-white/90 p-2 rounded-l shadow-md hover:bg-white transition-colors -mr-1"
                  aria-label="Next testimonial"
                >
                  <ChevronRight className="w-5 h-5 text-gray-700" />
                </button>
              </div>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-4">
              {testimonialsToUse.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentIndex ? 'bg-[#c88546]' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-3 gap-6 mt-12 max-w-3xl mx-auto">
            <div className="text-center">
              <p className="text-3xl font-bold text-[#c88546]">100+</p>
              <p className="text-sm text-gray-600">Homes Installed</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-[#c88546]">5.0★</p>
              <p className="text-sm text-gray-600">Google Rating</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-[#c88546]">10+</p>
              <p className="text-sm text-gray-600">Years Experience</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}